import keys from '@debbie/common/dist/cache/keys.js';
import { enteredAmount, isAssociationManaged, } from '@debbie/common/dist/economy/case-vouchers/index.js';
import { FixedDistributionAccount, FixedDistributionDateBehavior, getRemainingCapacity, maxSumAcrossPerspectives, } from '@debbie/common/dist/economy/fixed-distributions/index.js';
import { correctsExpired, isPayment, isWrittenDownInterest, } from '@debbie/common/dist/economy/helpers/vouchers.js';
import { VoucherTypeIds } from '@debbie/common/dist/economy/vouchers/index.js';
import { isCreditNote } from '@debbie/common/dist/economy/vouchers/matcher.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as fixedDistributionsAPI from '@debbie/cortex/dist/api/fixed-distributions.js';
import { caseVouchersEntity } from '@debbie/cortex/dist/case-vouchers/index.js';
import { fixedDistributionsEntity } from '@debbie/cortex/dist/fixed-distributions/index.js';
import q from '@debbie/cortex/dist/query.js';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { errorNotification } from '../../../utils/errors.js';
import Edit2 from '../../Icons/Edit2.js';
import { Modal } from '../../index.js';
import { CopyToClipboard } from '../../index.js';
import CaseVoucherDistributionsEdit from './CaseVoucherDistributionsEdit.js';
import { HeaderRow, IconButton, Section, } from './CaseVoucherDistributionsStyles.js';
import CaseVoucherDistributionsView from './CaseVoucherDistributionsView.js';
import CaseVoucherInfo from './CaseVoucherInfo.js';
const emptyChanges = () => ({
    edits: new Map(),
    additions: [],
    deletions: new Set(),
});
const hasPendingChanges = (changes) => changes.edits.size > 0 ||
    changes.additions.length > 0 ||
    changes.deletions.size > 0;
export const getEffectiveFds = (fixedDistributions, pending, voucher, isCredit) => {
    if (!fixedDistributions) {
        return [];
    }
    // Derived distributions give way to manual ones; an edit makes one manual.
    const effective = fixedDistributions
        .filter((fd) => !pending.deletions.has(fd.id) &&
        (!fd.derived || pending.edits.has(fd.id)))
        .map((fd) => {
        const edited = pending.edits.get(fd.id);
        return edited !== undefined ? { ...fd, amount: edited } : fd;
    });
    for (const addition of pending.additions) {
        if (!addition.toCaseVoucherId || addition.amount <= 0) {
            continue;
        }
        effective.push({
            id: addition.tempId,
            fromCaseVoucherId: voucher.caseVoucherId,
            toCaseVoucherId: addition.toCaseVoucherId,
            amount: addition.amount,
            account: FixedDistributionAccount.Both,
            dateBehavior: isCredit
                ? FixedDistributionDateBehavior.FromOriginal
                : FixedDistributionDateBehavior.FromApplied,
            associationId: null,
            derived: false,
            migratedId: null,
            tenantId: '',
            createdAt: '',
            createdBy: null,
            updatedAt: null,
            updatedBy: null,
        });
    }
    return effective;
};
export const getFdLimits = (effectiveFds, voucher, caseVouchers, editModeUnallocated, fdId, toVoucherId, account, currentAmount, targetErrorLabel) => {
    const toVoucher = caseVouchers.find((cv) => cv.caseVoucherId === toVoucherId);
    const sourceMax = currentAmount + Math.max(0, editModeUnallocated);
    if (!toVoucher) {
        return { max: sourceMax, targetError: null };
    }
    // Written-down interest takes at most what is alive at the replay, so its
    // stored amount does not bound what is kept to it.
    if (isWrittenDownInterest(toVoucher)) {
        return { max: Math.max(0, sourceMax), targetError: null };
    }
    // Capacity is the voucher's own amount, as the server checks it.
    const { targetAvailable } = getRemainingCapacity(effectiveFds, { caseVoucherId: voucher.caseVoucherId, amount: enteredAmount(voucher) }, {
        caseVoucherId: toVoucher.caseVoucherId,
        amount: enteredAmount(toVoucher),
    }, account, { excludeId: fdId });
    const targetMax = currentAmount + targetAvailable;
    const targetError = targetAvailable < 0 ? targetErrorLabel : null;
    return { max: Math.max(0, Math.min(sourceMax, targetMax)), targetError };
};
const computeCanSave = (pending, editModeUnallocated, voucher, fixedDistributions, effectiveFds, caseVouchers, targetErrorLabel) => {
    if (!hasPendingChanges(pending)) {
        return false;
    }
    if (editModeUnallocated < 0) {
        return false;
    }
    for (const dist of voucher.distribution || []) {
        if (!dist.fixedDistributionId) {
            continue;
        }
        const fd = fixedDistributions?.find((f) => f.id === dist.fixedDistributionId);
        if (!fd) {
            continue;
        }
        if (pending.deletions.has(fd.id)) {
            continue;
        }
        if (!pending.edits.has(fd.id)) {
            continue;
        }
        const editedAmount = pending.edits.get(fd.id);
        const { targetError } = getFdLimits(effectiveFds, voucher, caseVouchers, editModeUnallocated, fd.id, dist.caseVoucherId, fd.account, editedAmount, targetErrorLabel);
        if (targetError) {
            return false;
        }
    }
    for (const addition of pending.additions) {
        if (!addition.toCaseVoucherId || addition.amount <= 0) {
            continue;
        }
        const { targetError } = getFdLimits(effectiveFds, voucher, caseVouchers, editModeUnallocated, addition.tempId, addition.toCaseVoucherId, FixedDistributionAccount.Both, addition.amount, targetErrorLabel);
        if (targetError) {
            return false;
        }
    }
    return true;
};
const CaseVoucherInfoDistributions = ({ voucher, caseVouchers, fixedDistributions, singleCase, }) => {
    const lang = useSelector((s) => s.lang);
    const [infoVoucher, setInfoVoucher] = useState(null);
    const [pending, setPending] = useState(emptyChanges);
    const [isEditing, setIsEditing] = useState(false);
    const isDepositLike = isPayment(voucher) || voucher.voucherTypeId === VoucherTypeIds.CREDITNOTE;
    const isSourceVoucher = voucher.amount > 0;
    const canEdit = isDepositLike && isSourceVoucher && !isAssociationManaged(voucher);
    const isCredit = isCreditNote(voucher);
    const eligibleTargets = caseVouchers.filter((cv) => {
        if (cv.caseVoucherId === voucher.caseVoucherId) {
            return false;
        }
        if (cv.deletedAt) {
            return false;
        }
        if (cv.expired &&
            !correctsExpired(voucher, cv, FixedDistributionDateBehavior.FromOriginal)) {
            return false;
        }
        // Engine 2 interest is debt even when written down to zero.
        if (isSourceVoucher &&
            enteredAmount(cv) >= 0 &&
            !isWrittenDownInterest(cv)) {
            return false;
        }
        if (!isSourceVoucher && enteredAmount(cv) <= 0) {
            return false;
        }
        return true;
    });
    const effectiveFds = isEditing
        ? getEffectiveFds(fixedDistributions, pending, voucher, isCredit)
        : [];
    const sourceFds = effectiveFds.filter((fd) => fd.fromCaseVoucherId === voucher.caseVoucherId);
    const editModeUnallocated = isEditing
        ? Math.abs(voucher.amount) - maxSumAcrossPerspectives(sourceFds)
        : 0;
    const targetErrorLabel = lang.economy.voucherInfoDistributionRemaining;
    const canSave = isEditing &&
        computeCanSave(pending, editModeUnallocated, voucher, fixedDistributions, effectiveFds, caseVouchers, targetErrorLabel);
    const saveMutation = useMutation(async () => {
        const operations = [];
        for (const fdId of pending.deletions) {
            operations.push({ op: 'delete', id: fdId });
        }
        for (const [fdId, amount] of pending.edits) {
            if (!pending.deletions.has(fdId)) {
                operations.push({ op: 'update', id: fdId, amount });
            }
        }
        for (const addition of pending.additions) {
            if (!addition.toCaseVoucherId || addition.amount <= 0) {
                continue;
            }
            operations.push({
                op: 'create',
                toCaseVoucherId: addition.toCaseVoucherId,
                amount: addition.amount,
                account: FixedDistributionAccount.Both,
                dateBehavior: isCredit
                    ? FixedDistributionDateBehavior.FromOriginal
                    : FixedDistributionDateBehavior.FromApplied,
            });
        }
        if (operations.length === 0) {
            return;
        }
        await fixedDistributionsAPI.batch(voucher.caseVoucherId, { operations });
        q.invalidate(keys.write.create(fixedDistributionsEntity));
        q.invalidate(keys.write.modify(caseVouchersEntity, voucher.caseVoucherId));
        setPending(emptyChanges());
        setIsEditing(false);
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.fixedDistribution);
        },
    });
    const handleAmountChange = (fdId, amount) => {
        setPending((prev) => {
            const edits = new Map(prev.edits);
            edits.set(fdId, amount);
            return { ...prev, edits };
        });
    };
    const handleMarkForDeletion = (fdId) => {
        setPending((prev) => {
            const deletions = new Set(prev.deletions);
            deletions.add(fdId);
            return { ...prev, deletions };
        });
    };
    const handleUndoDeletion = (fdId) => {
        setPending((prev) => {
            const deletions = new Set(prev.deletions);
            deletions.delete(fdId);
            return { ...prev, deletions };
        });
    };
    const handleAddDistribution = () => {
        setPending((prev) => ({
            ...prev,
            additions: [
                ...prev.additions,
                {
                    tempId: crypto.randomUUID(),
                    toCaseVoucherId: '',
                    amount: 0,
                },
            ],
        }));
    };
    const handleUpdateAddition = (tempId, update) => {
        setPending((prev) => ({
            ...prev,
            additions: prev.additions.map((a) => a.tempId === tempId ? { ...a, ...update } : a),
        }));
    };
    const handleRemoveAddition = (tempId) => {
        setPending((prev) => ({
            ...prev,
            additions: prev.additions.filter((a) => a.tempId !== tempId),
        }));
    };
    const handleCancel = () => {
        setPending(emptyChanges());
        setIsEditing(false);
    };
    const hasDistributions = (voucher.distribution && voucher.distribution.length > 0) ||
        pending.additions.length > 0 ||
        isEditing;
    if (!hasDistributions && !canEdit) {
        return null;
    }
    const decoratedVouchers = caseVouchers.map((cv) => ({
        ...cv,
        children: [],
    }));
    return (React.createElement(Section, null,
        React.createElement(HeaderRow, null,
            React.createElement("h3", null, isSourceVoucher
                ? lang.economy.voucherInfoDistributedOnto
                : lang.economy.voucherInfoDistributionsReceived),
            canEdit && !isEditing && (React.createElement(IconButton, { onClick: () => setIsEditing(true), title: lang.economy.voucherInfoEditDistributions },
                React.createElement(Edit2, { size: "16px" })))),
        !isEditing && (React.createElement(CaseVoucherDistributionsView, { voucher: voucher, caseVouchers: caseVouchers, fixedDistributions: fixedDistributions, lang: lang, onShowVoucherInfo: setInfoVoucher })),
        isEditing && (React.createElement(CaseVoucherDistributionsEdit, { voucher: voucher, caseVouchers: caseVouchers, fixedDistributions: fixedDistributions, eligibleTargets: eligibleTargets, pending: pending, isCredit: isCredit, canSave: canSave, isSaving: saveMutation.isLoading, lang: lang, onAmountChange: handleAmountChange, onMarkForDeletion: handleMarkForDeletion, onUndoDeletion: handleUndoDeletion, onAddDistribution: handleAddDistribution, onUpdateAddition: handleUpdateAddition, onRemoveAddition: handleRemoveAddition, onSave: () => saveMutation.mutate(), onCancel: handleCancel, onShowVoucherInfo: setInfoVoucher })),
        infoVoucher && singleCase && (React.createElement(Modal, { header: React.createElement(React.Fragment, null,
                React.createElement(CopyToClipboard, { text: infoVoucher.caseVoucherId, hideIcon: true },
                    lang.economyVoucherTypeLabels[infoVoucher.voucherTypeId] || infoVoucher.label,
                    ' ',
                    infoVoucher.referenceId),
                ' ',
                "-",
                ' ',
                React.createElement(CopyToClipboard, { text: `${infoVoucher.id}` }, infoVoucher.id)), maxWidth: "800px", onToggle: () => setInfoVoucher(null) },
            React.createElement(CaseVoucherInfo, { caseVoucher: { ...infoVoucher, children: [] }, caseVouchers: decoratedVouchers, onDeleteVoucher: () => setInfoVoucher(null), singleCase: singleCase })))));
};
export default CaseVoucherInfoDistributions;
//# sourceMappingURL=CaseVoucherInfoDistributions.js.map